/// <reference types="resize-observer-browser" />

import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import {
  ActionBarConfig,
  presetAttributes,
  DynamicOverlay,
  i18nConfig,
  PrestoConfig,
  SearchBarConfig,
  prestoBranding,
  prestoChapters,
  blockAttributes,
  YoutubeConfig,
} from '../../../interfaces';
import { exitFullScreen } from '../../../util';
import cacheCues from './functions/cache-cues';
import createPlayer from './functions/create';
import customCSS from './functions/custom-css';
import hlsPreloadSize from './functions/hls-preload-size';
import mutedAutoplay from './functions/muted-autoplay';
import transform from './functions/transform-data';
import { getParents, isHLS, parseJSON } from './functions/util';

@Component({
  tag: 'presto-player',
  styleUrl: 'presto-player.scss',
  shadow: true,
})
export class PrestoPlayer {
  @Prop() iconUrl: string;
  @Prop() src: string;
  @Prop() type: string;
  @Prop({ mutable: true, reflect: true }) autoplay: boolean;
  @Prop() preload: 'metadata' | 'none' | 'auto';
  @Prop() poster: string;
  @Prop() playsinline: boolean;
  @Prop() isAdmin: boolean = false;
  @Prop() direction?: 'rtl';
  @Prop() css?: string;
  @Prop() currentTime: number;
  @Prop() mediaTitle: string;
  @Prop() classes: string;
  @Prop() audioAttributes: object;

  private _videoAttributes: any = {};
  @Prop()
  get videoAttributes(): object {
    return this._videoAttributes;
  }
  set videoAttributes(value: string | object) {
    this._videoAttributes = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  @Prop() markers: any;
  @Prop() automations: boolean;
  @Prop() providerVideoId: string;
  @Prop() videoId: number;
  @Prop() analytics: boolean;
  @Prop() provider: string;
  @Prop() lazyLoadYoutube: boolean;

  private _preset: any = {};
  @Prop()
  get preset(): presetAttributes {
    return this._preset;
  }
  set preset(value: string | presetAttributes) {
    this._preset = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  private _branding: any = {};
  @Prop()
  get branding(): prestoBranding {
    return this._branding;
  }
  set branding(value: string | prestoBranding) {
    this._branding = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  private _chapters: any = [];
  @Prop()
  get chapters(): prestoChapters {
    return this._chapters;
  }
  set chapters(value: string | prestoChapters) {
    this._chapters = typeof value === 'string' ? parseJSON(value, []) : value;
  }

  private _overlays: any = {};
  @Prop()
  get overlays(): Array<DynamicOverlay> {
    return this._overlays;
  }
  set overlays(value: string | Array<DynamicOverlay>) {
    this._overlays = typeof value === 'string' ? parseJSON(value, []) : value;
  }

  private _tracks: any = [];
  @Prop()
  get tracks(): { label: string; src: string; srcLang: string }[] {
    return this._tracks;
  }
  set tracks(value: string | Array<{ label: string; src: string; srcLang: string }>) {
    this._tracks = typeof value === 'string' ? parseJSON(value, []) : value;
  }

  private _blockAttributes: any = {};
  @Prop()
  get blockAttributes(): blockAttributes {
    return this._blockAttributes;
  }
  set blockAttributes(value: string | blockAttributes) {
    this._blockAttributes = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  private _config: any = {};
  @Prop()
  get config(): PrestoConfig {
    return this._config;
  }
  set config(value: string | PrestoConfig) {
    this._config = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  private _youtube: any = {};
  @Prop()
  get youtube(): YoutubeConfig {
    return this._youtube;
  }
  set youtube(value: string | YoutubeConfig) {
    this._youtube = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  private _actionBar: any = {};
  @Prop()
  get actionBar(): ActionBarConfig {
    return this._actionBar;
  }
  set actionBar(value: string | ActionBarConfig) {
    this._actionBar = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  private _i18n: any = {};
  @Prop()
  get i18n(): i18nConfig {
    return this._i18n;
  }
  set i18n(value: string | i18nConfig) {
    this._i18n = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  private _search: any = {};
  @Prop()
  get search(): SearchBarConfig {
    return this._search;
  }
  set search(value: string | SearchBarConfig) {
    this._search = typeof value === 'string' ? parseJSON(value, {}) : value;
  }

  /**
   * This element
   */
  @Element() el: HTMLElement;

  /**
   * Component loaded
   */
  @Event({ bubbles: true, composed: true }) loaded: EventEmitter<boolean>;

  @Event() currentMediaPlayer: EventEmitter<object>;
  @Event() playerReady: EventEmitter<object>;
  @Event() playedMedia: EventEmitter<object>;
  @Event() pausedMedia: EventEmitter<object>;
  @Event() endedMedia: EventEmitter<object>;

  /**
   * State() variables
   */
  @State() player: any;
  @State() playerEl?: HTMLVideoElement | HTMLElement;
  @State() shouldLazyLoad: boolean;
  @State() mutedPreview: boolean;
  @State() duration: number;
  @State() isSticky: boolean;
  @State() videoHeight: number;
  @State() playClass: string;
  @State() previouslyPlaying: boolean = false;
  @State() ctaActive: boolean = false;
  @State() emailActive: boolean = false;
  @State() actionBarActive: boolean = false;
  @State() ready: boolean;

  private intersectionObserver: IntersectionObserver;
  private resizeObserver: ResizeObserver;
  private overlaysComponent: HTMLPrestoDynamicOverlaysElement;
  private components: {
    cta: HTMLPrestoCtaOverlayElement;
    email: HTMLPrestoEmailOverlayElement;
    overlays: HTMLPrestoDynamicOverlaysElement;
    search: HTMLPrestoSearchBarElement;
  } = {
    cta: null,
    email: null,
    overlays: null,
    search: null,
  };

  /**
   * Play video
   * @returns Plyr
   */
  @Method()
  async play(unmute = false) {
    this.shouldLazyLoad = false;
    if (unmute) {
      this.mutedPreview = false;
    }
    this.player.play();
  }

  /**
   * Go to and play
   * @param time number
   */
  @Method()
  async goToAndPlay(time: number) {
    this.player.play();
    this.player.currentTime = time;
  }

  @Listen('currentMediaPlayer', { target: 'window' })
  handleScroll(ev) {
    if (ev.detail.id !== this.player.id) {
      if (!this.mutedPreview) {
        this.pause();
      }
    }
  }

  /**
   * Play video
   * @returns Plyr
   */
  @Method()
  async restart() {
    this.shouldLazyLoad = false;
    return this.player.restart();
  }

  /**
   * Pause video
   * @returns Plyr
   */
  @Method()
  async pause() {
    return this.player.pause();
  }

  /**
   * Pause video
   * @returns Plyr
   */
  @Method()
  async stop() {
    return this.player.stop();
  }

  /**
   * Toggle Fullscreen
   * @returns Plyr
   */
  @Method()
  async fullscreenToggle(open: boolean) {
    return this.player.fullscreen.toggle(open);
  }

  /**
   * Add an event listener for the specified event.
   * @param event String
   * @param func Function
   * @returns Plyr
   */
  @Method()
  async on(event: string, func: Function) {
    return this.player.on(event, func);
  }

  /**
   * Add an event listener for the specified event.
   * @param event String
   * @param func Function
   * @returns Plyr
   */
  @Method()
  async once(event: string, func: Function) {
    return this.player.once(event, func);
  }

  /**
   * Remove an event listener for the specified event.
   * @param event String
   * @param func Function
   * @returns Plyr
   */
  @Method()
  async off(event: string, func: Function) {
    return this.player.off(event, func);
  }

  @Watch('currentTime')
  async handleCurrentTimeChange(val) {
    if (this.player && Number.isInteger(val)) {
      // if we have duration
      // current time shouldn't be more than duration
      if (this.player.duration) {
        this.player.currentTime = Math.min(this.player.currentTime, this.player.duration);
      }

      // re-initialize if lazy loading.
      if (this.shouldLazyLoad) {
        this.shouldLazyLoad = false;
        await new Promise<void>(resolve => {
          setTimeout(async () => {
            await this.initialize();
            resolve();
          }, 50);
        });
      }

      // handle lag in embeds.
      if (this.player.embed && !this.player.playing) {
        this.player.currentTime = val;
        this.player.muted = true;
        this.player.play();
        this.player.once('timeupdate', () => {
          this.player.pause();
          this.player.currentTime = val;
          this.player.muted = false;
        });
      } else {
        this.player.currentTime = val;
      }
    }
  }

  /**
   * Handle sticky change
   */
  @Watch('isSticky')
  handleStickyChange() {
    const parents = getParents(this.el);
    parents.forEach(parent => {
      parent?.classList?.toggle('presto-sticky-parent', this.isSticky);
    });
  }

  /**
   * Get player config
   * @returns object
   */
  getConfig() {
    return {
      ...transform({
        preset: this.preset,
        chapters: this.chapters,
        branding: this.branding,
        analytics: !!this.analytics,
        automations: !!this.automations,
        autoplay: this.isAdmin ? false : !!this.autoplay,
        blockAttributes: this.blockAttributes,
        provider: this.provider,
        youtube: this.youtube,
        provider_video_id: this.providerVideoId,
        i18n: this.i18n,
        search: this.search,
      }),
      markers: { enabled: true, points: [] },
      ...(this.iconUrl
        ? {
            iconUrl: this.iconUrl,
          }
        : {}),
    };
  }

  /**
   * Get player data
   * @returns object
   */
  getPlayerData() {
    return {
      selector: this.playerEl,
      src: this.src || '',
      preload: this.preload,
      provider: this.provider,
      config: this.getConfig(),
      isAdmin: this.isAdmin,
    };
  }

  /**
   * Create the video player
   * @returns void
   */
  async createPlayer() {
    return await createPlayer(this.getPlayerData());
  }

  @Watch('playerEl')
  @Watch('player')
  handlePlayerElementChange() {
    hlsPreloadSize({
      src: this.src,
      el: this.playerEl,
      preload: this.preload,
      currentTime: this?.player?.currentTime,
    });
  }

  /**
   * Handle muted preview change
   * @returns void
   */
  @Watch('mutedPreview')
  handleMutedPreview(val, prev) {
    if (!this.player || this.isAdmin) {
      return;
    }

    // if it's been turned off
    if (!val && prev) {
      this.player.once('timeupdate', () => {
        this.player.loop = this.preset?.on_video_end === 'loop';
        this.player.toggleCaptions(this?.preset?.captions_enabled);
      });
    }

    if (!this.getConfig().blockAttributes?.mutedPreview?.enabled) {
      return;
    }

    // toggle muted autoplay in player
    mutedAutoplay({
      player: this.player,
      mutedPreview: this.mutedPreview,
      captions: !!this.blockAttributes?.mutedPreview?.captions,
      progress: this.getConfig().ajaxProgress,
      savePosition: this.getConfig().save_player_position,
      onPlay: () => this.play(),
    });
  }

  onPlayerReady() {
    // reobserve
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver.observe(this.el);
    }

    // on ready
    this.duration = this.player.duration;

    // skip for iOS
    var ua = window.navigator.userAgent;
    var iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    if (!iOS) {
      this.mutedPreview = this.blockAttributes?.mutedPreview?.enabled;
    }

    // inject UI
    this.renderDynamicOverlays();
    this.renderVideoCTA();
    this.renderEmailOverlay();
    this.renderSearchBar();

    this.player.on('timeupdate loadedmetadata', () => {
      this.duration = this.player.duration;
      this.renderDynamicOverlays();
    });

    if (isHLS(this.src)) {
      this.player.once('playing', () => {
        this.playerEl.style.height = null;
        this.playerEl.style.paddingBottom = null;
        this.playerEl.setAttribute('hls_loaded', '1');
      });
    }

    this.player.on('playing', () => (this.playClass = 'presto-player--playing'));
    this.player.on('pause', () => (this.playClass = 'presto-player--paused'));
    this.player.on('ended', () => (this.playClass = 'presto-player--ended'));

    this.player.on('playing', e => !this.mutedPreview && this.currentMediaPlayer.emit(e.detail.plyr));
    this.player.on('playing', e => this.playedMedia.emit(e.detail.plyr));
    this.player.on('pause', e => this.pausedMedia.emit(e.detail.plyr));
    this.player.on('ended', e => this.endedMedia.emit(e.detail.plyr));

    // handle go to start.
    if (this.preset?.on_video_end === 'go-to-start') {
      this.player.on('ended', () => {
        this.restart();
        if (this.provider === 'youtube') {
          this.stop();
        }
      });
    }

    cacheCues(this.player);
    setTimeout(() => {
      this.playerReady.emit(this.player);
      this.ready = true;
    }, 100);
  }

  /**
   * Update player state with events
   */
  handlePlayerEvents(player) {
    player.on('ready', e => {
      this.player = e.detail.plyr;
      this.onPlayerReady();
    });
  }

  /**
   * Handle lazy load changes
   * @returns
   */
  @Watch('shouldLazyLoad')
  handleLazyLoadChange() {
    if (this.provider !== 'youtube' || this.shouldLazyLoad || this.isAdmin) {
      return;
    }

    setTimeout(() => {
      this.onReload('play');
    }, 50);
  }

  /**
   * Should we lazy load the video?
   * @returns boolean
   */
  shouldLazyLoadVideo() {
    if (this.provider !== 'youtube' || this.autoplay) {
      return false;
    }

    return !!this.lazyLoadYoutube;
  }

  /**
   * Initialize data
   */
  componentWillLoad() {
    const children = this.el.children?.[0];
    children && children.classList && children.classList.contains('presto-iframe-fallback-container') && children.remove();
    this.i18n = window?.prestoPlayer?.i18n || window?.prestoPlayerAdmin?.i18n;

    // track visibility
    this.trackIntersection();
  }

  /**
   * Create Player
   */
  async componentDidLoad() {
    setTimeout(() => {
      this.initialize();
      this.loaded.emit(true);
    }, 0);
  }

  /**
   * Init player
   * @returns plyr object
   */
  async initialize() {
    const player = await this.createPlayer();
    this.handlePlayerEvents(player);
    this.syncVideoHeight();

    customCSS(this.el, this.css);

    return player;
  }

  renderSkins() {
    if (this.preset?.skin === 'modern') {
      return <presto-modern-skin></presto-modern-skin>;
    } else if (this.preset?.skin === 'business') {
      return <presto-business-skin></presto-business-skin>;
    } else if (this.preset?.skin === 'stacked') {
      return <presto-stacked-skin></presto-stacked-skin>;
    }
  }

  /**
   * On player reload
   * @param ev
   */
  async onReload(action) {
    if (!Object.keys(this.preset).length) {
      return;
    }

    const player = await this.initialize();

    if (action == 'play') {
      player.on('ready', () => {
        this.play();
        player.muted = true;
        player.muted = false;
        player.off('ready', this.play);
      });
    }
  }

  /**
   * Sync video height as height changes
   */
  async syncVideoHeight() {
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isSticky) {
        this.videoHeight = this.el.getBoundingClientRect().height;
      }
    });
    this.resizeObserver.observe(this.el);
  }

  @Listen('restartVideo')
  handleRestartVideo() {
    this.player.once('timeupdate', () => {
      setTimeout(() => this.play(), 500);
    });
    this.player.restart();
  }

  @Listen('playVideo')
  handlePlayVideo() {
    this.play();
  }

  @Listen('pauseVideo')
  handlePauseVideo(e) {
    this.pause();
    if (e.detail) {
      exitFullScreen(this.player);
    }
  }

  /**
   * Handle tab visibility change
   * @returns void
   */
  @Listen('visibilitychange', { target: 'window' })
  playVideoOnlyInViewport() {
    if (this.isAdmin || !this.preset?.play_video_viewport || !this.player) {
      return;
    }

    if (document.visibilityState === 'visible') {
      this.intersectionObserver.observe(this.el);
    } else {
      if (this.player.playing) {
        this.previouslyPlaying = true;
      }
      this.pause();
      this.intersectionObserver.disconnect();
    }
  }

  /**
   * Tracks the visibility of the video
   * based on intersection
   */
  trackIntersection() {
    this.intersectionObserver = new IntersectionObserver(entries => {
      this.handleVisibilityIntersection(entries?.[0]);
    });
    this.intersectionObserver.observe(this.el);
  }

  /**
   * Handle the intersection
   * @param element
   * @returns
   */
  handleVisibilityIntersection(element) {
    if (!element) return;

    // handle sticky
    this.handleStickyScroll(element);
    // handle viewport
    this.handleVisibilityPlayChange(element);
  }

  /**
   * Handle sticky scroll based on intersection
   */
  handleStickyScroll(element) {
    if (!this.preset?.sticky_scroll) return;

    // not in admin, muted preview or if not playing
    if (this.isAdmin || this.mutedPreview || !this?.player?.playing) {
      this.isSticky = false;
      return;
    }

    let rect = element.boundingClientRect;
    this.isSticky = rect.height < -rect.top;

    // hide again when paused
    if (document.visibilityState === 'visible') {
      if (this.isSticky && this.player) {
        const namedListener = () => {
          window.removeEventListener('scroll', namedListener);
          if (this.player.paused) {
            this.isSticky = false;
          }
        };

        this.player.once('pause', () => {
          window.addEventListener('scroll', namedListener);
        });
      }
    }
  }

  /**
   * Handle play change on visibility condition
   * @param condition
   * @returns
   */
  handleVisibilityPlayChange(element) {
    if (this.isAdmin || !this.preset?.play_video_viewport) {
      return;
    }

    // handle autoplay before load
    if (!this.player && this.getConfig().autoplay) {
      this.previouslyPlaying = true;
      this.autoplay = element?.isIntersecting ? true : false;
    }

    if (!this.player) return;

    // intersecting
    if (element?.isIntersecting) {
      if (this.getConfig().autoplay) {
        this.autoplay = true;
      }
      this.previouslyPlaying && this.play();
      this.previouslyPlaying = false;
      return;
    }

    // not intersecting
    if (document.visibilityState === 'visible') {
      if (this.isSticky) {
        return;
      }
    }

    // handle muted preview
    if (this.blockAttributes?.mutedPreview?.enabled) {
      this.previouslyPlaying = true;
      this.pause();
      this.player.once('playing', () => {
        this.previouslyPlaying = true;
        this.pause();
      });
      return;
    }

    if (this.player.playing) {
      this.previouslyPlaying = true;
    }
    this.pause();
  }

  /**
   * Render the muted overlay
   * @returns JSX
   */
  renderMutedOverlay() {
    if (!this.player || !this.mutedPreview || this.isAdmin) {
      return;
    }

    return (
      <presto-muted-overlay
        mutedOverlay={this.getConfig().blockAttributes?.mutedOverlay}
        preset={this.preset}
        onPlayVideo={() => {
          this.mutedPreview = false;
          this.play();
        }}
      ></presto-muted-overlay>
    );
  }

  /**
   * Render the video
   * @returns JSX
   */
  renderVideo() {
    switch (this.provider) {
      case 'vimeo':
        return <presto-vimeo player={this.player} getRef={el => (this.playerEl = el as HTMLElement)} poster={this.poster} src={this.src}></presto-vimeo>;
      case 'youtube':
        return (
          <presto-youtube
            onReload={ev => this.onReload(ev?.detail)}
            player={this.player}
            getRef={el => (this.playerEl = el as HTMLElement)}
            lazyLoad={this.shouldLazyLoadVideo()}
            poster={this.poster}
            src={this.src}
          ></presto-youtube>
        );
      case 'bunny':
        return (
          <presto-bunny
            getRef={el => (this.playerEl = el as HTMLVideoElement)}
            player={this.player}
            autoplay={this.autoplay}
            preload={this.preload}
            poster={this.poster}
            playsinline={this.playsinline}
            src={this.src}
            tracks={this.tracks}
            videoAttributes={this.videoAttributes}
          ></presto-bunny>
        );
      case 'audio':
        return (
          <presto-audio
            getRef={el => (this.playerEl = el as HTMLAudioElement)}
            player={this.player}
            autoplay={this.autoplay}
            preload={this.preload}
            poster={this.poster}
            preset={this.preset}
            src={this.src}
            tracks={this.tracks}
            mediaTitle={this.mediaTitle}
            audioAttributes={this.audioAttributes}
          ></presto-audio>
        );
      default:
        return (
          <presto-video
            getRef={el => (this.playerEl = el as HTMLVideoElement)}
            player={this.player}
            autoplay={this.autoplay}
            preload={this.preload}
            poster={this.poster}
            playsinline={this.playsinline}
            src={this.src}
            tracks={this.tracks}
            videoAttributes={this.videoAttributes}
          ></presto-video>
        );
    }
  }

  renderVideoCTA() {
    if (!window?.prestoPlayer?.isPremium) return;
    if (this.isAdmin) return;
    if (!this.preset?.cta?.enabled) return;
    if (this.components.cta?.offsetParent) return;
    if (this.provider === 'audio') return;

    this.components.cta = document.createElement('presto-cta-overlay');
    this.components.cta.direction = this.direction;
    this.components.cta.player = this.player;
    this.components.cta.preset = this.preset;
    this.components.cta.i18n = this.i18n;

    this.player.elements.container.append(this.components.cta);
  }

  renderAudioCTA() {
    if (this.provider !== 'audio') return;
    if (!this.player) return;
    if (!this.preset?.cta?.enabled) return;
    return <presto-cta-overlay direction={this.direction} player={this.player} preset={this.preset} i18n={this.i18n} provider={this.provider}></presto-cta-overlay>;
  }

  /**
   * Render email overlay
   * @returns JSX
   */
  renderEmailOverlay() {
    if (!window?.prestoPlayer?.isPremium || this.isAdmin || window?.prestoPlayer?.logged_in) {
      return;
    }
    if (this.components.email?.offsetParent) {
      return;
    }
    if (this.provider === 'audio') return;

    this.components.email = document.createElement('presto-email-overlay');
    this.components.email.direction = this.direction;
    this.components.email.player = this.player;
    this.components.email.preset = this.preset;
    this.components.email.i18n = this.i18n;
    this.components.email.videoId = this.videoId;
    this.components.email.provider = this.provider;

    this.player.elements.container.append(this.components.email);
  }

  renderAudioEmail() {
    if (this.provider !== 'audio') return;
    if (!this.player) return;
    if (!window?.prestoPlayer?.isPremium || this.isAdmin || window?.prestoPlayer?.logged_in) return;
    return <presto-email-overlay direction={this.direction} player={this.player} preset={this.preset} i18n={this.i18n} videoId={this.videoId} provider={this.provider}></presto-email-overlay>;
  }
  /**
   * Render email overlay
   * @returns JSX
   */
  renderActionBar() {
    if (!this.preset?.action_bar?.enabled || !this.player) {
      return;
    }
    return <presto-action-bar config={this.preset.action_bar} player={this?.player} youtube={this.youtube}></presto-action-bar>;
  }

  /**
   * Render search overlay
   * @returns JSX
   */
  renderSearchBar() {
    if (!this.preset?.search?.enabled) {
      return;
    }
    this.components.search = document.createElement('presto-search-bar');
    this.components.search.player = this.player;
    this.player.elements.container.append(this.components.search);
  }

  stickyPositionClass() {
    if (!this.isSticky) {
      return '';
    }

    return !!this.preset?.sticky_scroll_position ? `presto-sticky--${this.preset?.sticky_scroll_position.replace(/\s+/g, '-')}` : 'presto-sticky--bottom-right';
  }

  // Poster can resolve after the player inits (e.g. private Bunny thumbnails
  // need an async signed/blob URL). data-poster is only read once at init, so
  // push later changes straight to Plyr.
  @Watch('poster')
  updatePoster(newPoster: string) {
    if (this.player && newPoster) {
      this.player.poster = newPoster;
    }
  }

  /** We append this instead of using JSX because we want it to work in fullscreen. */
  @Watch('overlays')
  @Watch('preset')
  renderDynamicOverlays() {
    if (!this.player) {
      return;
    }

    // is not visible, recreate.
    if (!this?.overlaysComponent?.offsetParent) {
      this.overlaysComponent = document.createElement('presto-dynamic-overlays');
      this.overlaysComponent.addEventListener(
        'reloadComponent',
        () => {
          this.overlaysComponent.remove();
          this.overlaysComponent = document.createElement('presto-dynamic-overlays');
        },
        { once: true },
      );
    }

    this.overlaysComponent.style.cssText = 'display: block !important; opacity: 1 !important; visibility: visible !important;';
    this.overlaysComponent.overlays = this.overlays;
    this.overlaysComponent.preset = this.preset;
    this.overlaysComponent.player = this.player;
    this.overlaysComponent.enabled = !this.player?.config?.mutedPreview?.enabled;

    // has not yet been appended.
    if (!this?.overlaysComponent?.offsetParent) {
      this.player.elements?.wrapper?.append(this.overlaysComponent);
    }
  }

  @Listen('ctaStateChange')
  handleCtaStateChange(ev) {
    this.ctaActive = ev.detail;
  }

  @Listen('emailStateChange')
  handleEmailStateChange(ev) {
    this.emailActive = ev.detail;
  }
  @Listen('actionBarStateChange')
  handleActionBarStateChange(ev) {
    this.actionBarActive = ev.detail;
  }

  /**
   * Clean up resources when component is disconnected
   */
  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  /**
   * Render the component
   * @returns JSX
   */
  render() {
    return (
      <Host style={{ height: this.isSticky ? `${this.videoHeight}px` : 'auto' }} class={{ ready: this.ready }}>
        <div
          part="wrapper"
          dir={this.direction}
          class={`presto-player__wrapper fitvidsignore
          presto-video-id-${this.videoId}
        ${!!this.isSticky ? (this.provider === 'audio' ? 'presto-sticky-audio' : 'presto-sticky') : ''}
        ${this.provider !== 'audio' ? this.stickyPositionClass() : ''}
        ${this.direction === 'rtl' ? 'rtl' : ''}
        ${this.ctaActive === true ? 'cta-active' : ''}
        ${this.emailActive === true ? 'email-active' : ''}
        ${this.actionBarActive === true && this.preset?.action_bar?.enabled ? 'action-bar-active' : ''}
        ${!!this.preset?.skin && `skin-${this.preset?.skin}`}
        ${!!this.preset?.hide_youtube ? 'hide-youtube-ui' : ''}
        ${!!this.preset?.caption_style ? `caption-style-${this.preset?.caption_style}` : ''}
        ${!!this.mutedPreview ? 'is-muted-overlay' : ''}
        ${this.playClass ? this.playClass : ''}
        ${this.preset?.play && !this.preset['rewind'] ? 'rewind-inactive' : ''}
        ${this.classes || ''}`}
        >
          <div>
            <slot name="player-start" />

            <slot name="player-before-video" />

            {this.renderVideo()}

            <slot name="player-after-video" />

            {this.renderActionBar()}

            {this.renderMutedOverlay()}

            {this.renderAudioCTA()}

            {this.renderAudioEmail()}

            {this.renderSkins()}

            <slot name="player-end" />
          </div>
        </div>
      </Host>
    );
  }
}
